import React, { useEffect, useState } from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container as ContainerBase } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import logo from "images/logo.png";
import { ReactComponent as LockIcon } from "feather-icons/dist/icons/lock.svg";
import { resetPassword } from "api/auth";

const Container = tw(ContainerBase)`min-h-screen bg-pbsGreen-900 text-white font-medium flex justify-center -m-8`;
const Content = tw.div`max-w-screen-xl m-0 sm:mx-20 sm:my-16 bg-white text-gray-900 shadow sm:rounded-lg flex justify-center flex-1`;
const MainContainer = tw.div`lg:w-1/2 xl:w-5/12 p-6 sm:p-12 mx-auto`;
const LogoLink = tw.a``;
const LogoImage = tw.img`h-12 mx-auto`;
const MainContent = tw.div`mt-12 flex flex-col items-center`;
const Heading = tw.h1`text-2xl xl:text-3xl font-extrabold`;
const Subheading = tw.p`mt-4 text-sm text-gray-600 text-center max-w-xs`;
const FormContainer = tw.div`w-full flex-1 mt-8`;

const Form = tw.form`mx-auto max-w-xs`;
const Input = tw.input`w-full px-8 py-4 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white mt-5 first:mt-0`;
const SubmitButton = styled.button`
  ${tw`mt-5 tracking-wide font-semibold bg-pbsGreen-500 text-gray-100 w-full py-4 rounded-lg hover:bg-pbsGreen-900 transition-all duration-300 ease-in-out flex items-center justify-center focus:shadow-outline focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
  .icon {
    ${tw`w-6 h-6 -ml-2`}
  }
  .text {
    ${tw`ml-3`}
  }
`;

export default function ResetPassword({
  logoLinkUrl = "/",
  headingText = "Reset Your Password",
  subheadingText = "Enter a new password for your account below.",
  submitButtonText = "Reset Password",
  SubmitButtonIcon = LockIcon,
  loginUrl = "/login",
  forgotPasswordUrl = "/forgot-password",
  minPasswordLength = 8,
}) {
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pull the token out of the URL's query string, e.g. /reset-password?token=abc123
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get("token");
    setToken(tokenFromUrl || "");

    if (!tokenFromUrl) {
      setErrorMessage(
        "This password reset link is missing or invalid. Please request a new one."
      );
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    setSuccessMessage("");
    setErrorMessage("");

    if (!token) {
      setErrorMessage(
        "This password reset link is missing or invalid. Please request a new one."
      );
      return;
    }

    if (password.length < minPasswordLength) {
      setErrorMessage(`Password must be at least ${minPasswordLength} characters long.`);
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);

    resetPassword({ token, password })
      .then((response) => {
        if (response.success) {
          setSuccessMessage("Your password has been reset successfully. You can now sign in.");
          setPassword("");
          setConfirmPassword("");
        } else {
          setErrorMessage(
            "Reset failed" + (response.message ? ": " + response.message : ".")
          );
        }
      })
      .catch((error) => {
        console.error("Password reset failed:", error);
        setErrorMessage("Something went wrong. Please try again later.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <AnimationRevealPage>
      <Container>
        <Content>
          <MainContainer>
            <LogoLink href={logoLinkUrl}>
              <LogoImage src={logo} />
            </LogoLink>
            <MainContent>
              <Heading>{headingText}</Heading>
              <Subheading>{subheadingText}</Subheading>
              <FormContainer>
                <Form onSubmit={handleSubmit}>
                  <Input
                    type="password"
                    placeholder="New Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isSubmitting || !token}
                  />
                  <Input
                    type="password"
                    placeholder="Confirm New Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isSubmitting || !token}
                  />
                  <SubmitButton type="submit" disabled={isSubmitting || !token}>
                    <SubmitButtonIcon className="icon" />
                    <span className="text">
                      {isSubmitting ? "Resetting..." : submitButtonText}
                    </span>
                  </SubmitButton>
                </Form>
                {successMessage && (
                  <p tw="mt-4 text-green-600 text-sm text-center">{successMessage}</p>
                )}
                {errorMessage && (
                  <p tw="mt-4 text-red-600 text-sm text-center">{errorMessage}</p>
                )}
                <p tw="mt-8 text-sm text-gray-600 text-center">
                  Need a new link?{" "}
                  <a href={forgotPasswordUrl} tw="border-b border-gray-500 border-dotted">
                    Request Password Reset
                  </a>
                </p>
                <p tw="mt-2 text-sm text-gray-600 text-center">
                  Remembered your password?{" "}
                  <a href={loginUrl} tw="border-b border-gray-500 border-dotted">
                    Sign In
                  </a>
                </p>
              </FormContainer>
            </MainContent>
          </MainContainer>
        </Content>
      </Container>
    </AnimationRevealPage>
  );
}
